import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import {
  LicensedAgency,
  CustomerInterface,
} from 'contexts/CurrentCustomerContext'
import { ColumnDef, MenuClick } from 'cs-kevlar-react/lib/es/table/base'
import {
  Table,
  Box,
  ContentSection,
  OutlinedButton,
  Typography,
  Dot,
  ConfirmationModal,
} from 'cs-kevlar-react'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import { EL400, EL600, N100 } from 'cs-kevlar-react/lib/es/theming/palette'
import {
  AdvancedAuthenticationType,
  InterfaceType,
  InterfaceHealthColor,
  CertificateRevocationStatus,
  InterfaceApiVersion,
  InterfaceFeatureFlag,
  CustomerProductLine,
  CustomerInterfaceLogLevel,
  WebAdminUser,
  SupportUserPermission,
  InterfaceFeature,
  CustomerCertificateViewModel,
} from '../../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { useParams, useNavigate } from 'react-router'
import {
  useCustomerApi,
  useConnectorApi,
  usePageNotifications,
  useAuthApi,
  useAuth,
  useCustomerHealthSync,
  useCurrentCustomer,
  useDynamicPageTitle,
  useLogger,
  useCurrentAgency,
  useFormFactor,
} from 'hooks'
import {
  PageTitleRightActions,
  SidePanelContent,
  ConnectorHealthBadge,
  PageTitleSubtitle,
} from 'components'
import { EditAgency } from 'components/agencies'
import {
  EditConnector,
  RenewCertificateModal,
  DownloadCertificateModal,
  ConnectorHealth,
  InterfaceConfiguration,
  CertificateRevocationStatusModal,
} from 'components/connectors'
import {
  matchIdOrDefault,
  considerDataToBeStale,
  getAdvancedAuthenticationTypeLabel,
  getConnectorTypeLabel,
  getCustomerProductLineLabel,
  serverDateToLocaleDateString,
  getCertificateRevocationStatusLabel,
} from 'utils'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { buildNavigationUrl as buildAgencyUserNavigationUrl } from '../licensedAgency/Users'
import { EditImportConfiguration } from 'components/diagnostics/EditImportConfiguration'
import clsx from 'clsx'
import { CertificateHistory } from 'components/CertificateHistory'
import {
  FilterOptions,
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

type CertActionType =
  | 'ConfirmingCertDownload'
  | 'ShowingCertHistoryModal'
  | 'ConfirmingCertRevoke'
  | 'ConfirmingCertReinstate'
  | null
type CertActionState = {
  type: CertActionType
  interface: CustomerInterface
}

type CustomerProps = {
  customerAgencyId: string
  licensedAgencyId?: string
  customerInterfaceId?: string
  action?: string
}

const AGENCIES_FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: false,
}

const AGENCIES_PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
  disablePaging: true,
  singularAdjective: 'agency',
  pluralAdjective: 'agencies',
}

const CONNECTORS_FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: false,
}

const CONNECTORS_PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
  disablePaging: true,
  singularAdjective: 'connector',
  pluralAdjective: 'connectors',
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    connectorHealth: {
      paddingLeft: theme.spacing(3),
    },
    certStatus: {
      marginTop: theme.spacing(8),
    },
    certRevokedStatus: {
      fontWeight: 'bold',
      color: EL600,
      textTransform: 'uppercase',
    },
    agencySubTitle: {
      color: N100,
      marginBottom: theme.spacing(10),
    },
  }),
)

type ValidAgencyMenuKey = 'Assist' | 'EditAgency'
type ValidConnectorMenuKey =
  | 'EditConnector'
  | 'RenewCertificate'
  | 'ReinstateCertificate'
  | 'RevokeCertificate'
  | 'DownloadCertificate'
  | 'CertificateHistory'
  | 'ImplementerLogs'
  | 'NebulaLogs'
  | 'ConnectorHealth'
  | 'SupportDashboard'
  | 'EditConfiguration'
  | 'EditImportConfiguration'

const agencyColumns: ColumnDef<LicensedAgency, ValidAgencyMenuKey>[] = [
  {
    label: 'Name',
    sortable: false,
    sortByDefault: true,
    filterable: false,
    dataProperty: 'name' as keyof LicensedAgency,
  },
  {
    label: 'Agency ID',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    dataProperty: 'uniqueIdentifier' as keyof LicensedAgency,
  },
  {
    label: 'Licenses',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    dataProperty: 'licenseCount' as keyof LicensedAgency,
  },
  {
    label: 'AA Version',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    dataFunc: (row: LicensedAgency) =>
      getAdvancedAuthenticationTypeLabel(row.advancedAuthType),
  },
  {
    label: '',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    menuGroups: [
      {
        options: [
          {
            label: 'Assist',
            key: 'Assist',
          },
          {
            label: 'Edit',
            key: 'EditAgency',
          },
        ],
      },
    ],
  },
]

export const buildNavLink = (id: string) => {
  return `/customers/${id}/details`
}

export const buildNavLinkForAgency = (id: string, agency: LicensedAgency) => {
  return `/customers/${id}/details/agency/${agency.id}`
}

export const buildNavLinkForConnector = (
  id: string,
  connector: CustomerInterface,
  action?: string,
) => {
  if (!action) return `/customers/${id}/details/connector/${connector.id}`

  return `/customers/${id}/details/connector/${connector.id}/${action}`
}

const defaultAgency: LicensedAgency = {
  id: 'NEW',
  licensedAgencyId: '',
  customerAgencyId: '',
  documentId: '',
  externalId: '',
  externalAgencyList: [],
  name: '',
  licenseCount: 0,
  uniqueIdentifier: '',
  advancedAuthType: AdvancedAuthenticationType.CompensatingControls,
  notes: '',
  interconnectedCadConfiguration: null,
  globalConfiguration: null,
}

const defaultConnector: CustomerInterface = {
  id: 'NEW',
  customerInterfaceId: '',
  interfaceType: InterfaceType.ProSuite,
  customerAgencyId: '',
  name: '',
  notes: '',
  interfaceRegistration: {
    apiVersion: InterfaceApiVersion.v2_0_0,
    properties: {},
    featureFlags: InterfaceFeatureFlag.GeneralAvailability,
    enabledFeatures: [],
    supportsNewInterfaceFeatures: true,
  },
  errorMessage: '',
  healthState: InterfaceHealthColor.Black,
  certificateRevocationStatus: CertificateRevocationStatus.Ok,
  certificateHistory: [],
  createdOn: '',
  configurationOverrides: {},
  importConfiguration: null,
}

type MenuEls = {
  [key: string]: HTMLElement | null
}

const interfaceSupportsRuntimeConfigurationChanges = (
  connector: CustomerInterface,
) => {
  if (
    !connector.interfaceRegistration ||
    !connector.interfaceRegistration.enabledFeatures
  )
    return false

  return (
    connector.interfaceRegistration.enabledFeatures.filter(
      (x) => x === InterfaceFeature.RuntimeConfigurationOverride,
    ).length > 0
  )
}

const canViewInterfaceConfiguration = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(SupportUserPermission.ViewInterfaceConfiguration)
  )
}

const canManageInterfaceConfiguration = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(
      SupportUserPermission.ManageInterfaceConfiguration,
    )
  )
}

const useChipStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      marginLeft: theme.spacing(8),
    },
  }),
)

const doesConnectorHaveOverrides = (configs: { [key: string]: string }) => {
  if (!configs) return false

  const keys = Object.keys(configs)

  if (keys.length === 0) return false

  if (keys.length === 1 && keys[0] === 'CustomerInterfaceLogging') return false

  return true
}

const CustomerDetails: React.FunctionComponent = (props) => {

  const params = useParams<CustomerProps>()
  if(!params || !params.customerAgencyId) return null
  
  const customerAgencyId = params.customerAgencyId as string

  const classes = useStyles({})
  const navigate = useNavigate()
  const { logError } = useLogger()
  const formFactor = useFormFactor()  

  const {
    agencies,
    connectors,
    dispatch,
    customerContext,
  } = useCurrentCustomer(params.customerAgencyId)

  const customerName = customerContext.currentCustomer?.customer.name
  const auditTitle = !!customerName
    ? `Customer Detail / ${customerName}`
    : undefined
  usePageViewAudit(auditTitle, customerContext.currentCustomer != null, true)

  const { getCustomerDetails, startAssisting } = useCustomerApi()
  const {
    getConnectorImportConfiguration,
    updateConnectorImportConfiguration,
  } = useConnectorApi()
  const { dispatch: dispatchNotification } = usePageNotifications()
  const { getCurrentSession } = useAuthApi()
  const { dispatch: dispatchAuthAction, user: authUser } = useAuth()
  const { dispatch: dispatchAgencyAction } = useCurrentAgency(null)

  useCustomerHealthSync(params.customerAgencyId)

  const {
    state: pageState,
    dispatch: dispatchPageTitle,
  } = useDynamicPageTitle()

  const [
    certActionState,
    setCertActionState,
  ] = React.useState<CertActionState | null>(null)

  const [menuEls, setMenuEls] = React.useState<MenuEls>({})

  const handleMenuClose = (id: string) => {
    setMenuEls({
      ...menuEls,
      [id]: null,
    })
  }

  const selectAgencyForEditing = (agency: LicensedAgency | null) => {
    if (agency === null) {
      navigate(buildNavLink(customerAgencyId))
      return
    }

    navigate(buildNavLinkForAgency(customerAgencyId, agency))
  }

  const selectConnectorForEditing = (connector: CustomerInterface | null) => {
    if (connector === null) {
      navigate(buildNavLink(customerAgencyId))
      return
    }

    navigate(
      buildNavLinkForConnector(customerAgencyId, connector),
    )
  }

  const stopEditingCustomer = () => {
    navigate(buildNavLink(customerAgencyId))
  }

  const handleAgencyMenuClick = (
    key: ValidAgencyMenuKey,
    row: LicensedAgency,
  ) => {
    handleMenuClose(row.id)

    if (key === 'EditAgency') {
      navigate(buildNavLinkForAgency(customerAgencyId, row))
      return
    }

    if (key === 'Assist') {
      dispatchAgencyAction({
        type: 'Clear',
      })

      startAssisting(row.customerAgencyId, row.licensedAgencyId)
        .then((response) => {
          if (!response) return

          if (response.status === 200) {
            getCurrentSession(dispatchAuthAction).then(() => {
              navigate(
                buildAgencyUserNavigationUrl(null, {
                  licensedAgencyId: row.licensedAgencyId,
                  customerAgencyId: row.customerAgencyId,
                }),
              )
            })
          } else {
            dispatchNotification({
              type: 'SetError',
              payload: 'There was an error assisting the agency',
            })
          }
        })
        .catch((error) => {
          logError(error)

          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error assisting the agency',
          })
        })
    }
  }

  const cancelCertDownload = () => {
    setCertActionState(null)
  }

  const handleConnectorMenuClick = (
    key: ValidConnectorMenuKey,
    row: CustomerInterface,
  ) => {
    handleMenuClose(row.id)
    const certAction: CertActionState = {
      type: null,
      interface: row,
    }

    if (key === 'EditConnector') {
      navigate(buildNavLinkForConnector(customerAgencyId, row))
      return
    }

    if (key === 'RenewCertificate') {
      navigate(
        buildNavLinkForConnector(customerAgencyId, row, 'renew'),
      )
      return
    }

    if (key === 'ReinstateCertificate') {
      certAction.type = 'ConfirmingCertReinstate'
      setCertActionState(certAction)
    }

    if (key === 'RevokeCertificate') {
      certAction.type = 'ConfirmingCertRevoke'
      setCertActionState(certAction)
    }

    if (key === 'DownloadCertificate') {
      certAction.type = 'ConfirmingCertDownload'
      setCertActionState(certAction)
    }

    if (key === 'CertificateHistory') {
      certAction.type = 'ShowingCertHistoryModal'
      setCertActionState(certAction)
    }

    if (key === 'SupportDashboard' && row.customerAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/dashboards/customer/${row.customerAgencyId}/interface/${row.id}`
      link.target = '_blank'
      link.click()
    }

    if (key === 'ImplementerLogs' && row.customerAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/logs/customer/${row.customerAgencyId}/connectorImplementer?customerInterfaceId=${row.id}`
      link.target = '_blank'
      link.click()
    }

    if (key === 'NebulaLogs' && row.customerAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/logs/customer/${row.customerAgencyId}/nebula?customerInterfaceId=${row.id}`
      link.target = '_blank'
      link.click()
    }

    if (key === 'ConnectorHealth' && row.customerAgencyId) {
      navigate(
        buildNavLinkForConnector(customerAgencyId, row, 'health'),
      )
      return
    }

    if (key === 'EditConfiguration' && row.customerAgencyId) {
      navigate(
        buildNavLinkForConnector(customerAgencyId, row, 'config'),
      )
      return
    }

    if (key === 'EditImportConfiguration' && row.customerAgencyId) {
      navigate(
        buildNavLinkForConnector(
          customerAgencyId,
          row,
          'config-import',
        ),
      )
      return
    }
  }

  const editingAgency = matchIdOrDefault(
    customerContext.currentCustomer?.agencies,
    params.licensedAgencyId,
    defaultAgency,
  )

  const isEditingAgency = editingAgency !== null && editingAgency.id !== 'NEW'
  const isAddingAgency = editingAgency !== null && editingAgency.id === 'NEW'

  const editingConnector = matchIdOrDefault(
    customerContext.currentCustomer?.connectors,
    params.customerInterfaceId,
    defaultConnector,
  )

  const isEditingConnector =
    editingConnector !== null &&
    editingConnector.id !== 'NEW' &&
    !params.action
  const isAddingConnector =
    editingConnector !== null &&
    editingConnector.id === 'NEW' &&
    !params.action
  const isRenewingCertificateForConnector =
    editingConnector !== null && params.action === 'renew'
  const isViewingHealthForConnector =
    editingConnector !== null && params.action === 'health'
  const isViewingConfigurationForConnector =
    editingConnector !== null && params.action === 'config'
  const isViewingImportConfigurationForConnector =
    editingConnector !== null && params.action === 'config-import'

  const connectorColumns: ColumnDef<
    CustomerInterface,
    ValidConnectorMenuKey
  >[] = React.useMemo(
    () => [
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        padding: 'none',
        align: 'right',
        // 16px plus 4 for the right aligned badge which is half in and half out
        width: Spacing * 10,
        component: ({ row }) => {
          const classes = useStyles({})

          return (
            <ConnectorHealthBadge
              health={row.healthState}
              className={classes.connectorHealth}
            />
          )
        },
      },
      {
        label: 'Name',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        dataProperty: 'name' as keyof CustomerInterface,
        component: ({ row }) => {
          const classes = useChipStyles({})
          return (
            <Box>
              <Typography display="inline">{row.name}</Typography>
              {doesConnectorHaveOverrides(row.configurationOverrides) && (
                <Dot
                  className={classes.chip}
                  enableTooltip
                  label="Config Overridden"
                  color={EL400}
                />
              )}
            </Box>
          )
        },
      },
      {
        label: 'Created',
        sortable: false,
        sortByDefault: false,
        sortValueFunc: (row: CustomerInterface) =>
          serverDateToLocaleDateString(row.createdOn),
        filterable: true,
        dataFunc: (row: CustomerInterface) =>
          serverDateToLocaleDateString(row.createdOn),
      },
      {
        label: 'Certificate Status',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        component: ({ row }) => {
          if (
            row.certificateRevocationStatus ===
            CertificateRevocationStatus.Revoked
          ) {
            const classes = useStyles({})
            return (
              <Typography
                className={classes.certRevokedStatus}
                display="inline"
              >
                {getCertificateRevocationStatusLabel(
                  row.certificateRevocationStatus,
                )}
              </Typography>
            )
          }

          return (
            <Typography display="inline">
              {getCertificateRevocationStatusLabel(
                row.certificateRevocationStatus,
              )}
            </Typography>
          )
        },
      },
      {
        label: 'Certificate Expiration',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        dataFunc: (row: CustomerInterface) => {
          const certs = row.certificateHistory ?? []
          const nonRevokedCerts = certs.filter((cert) => !cert.revoked)
          if (nonRevokedCerts.length === 0) return '--'

          return serverDateToLocaleDateString(nonRevokedCerts[0].expiresOn)
        },
      },
      {
        label: 'Downloaded',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        dataFunc: (row: CustomerInterface) => {
          if (row.downloadedOn) {
            const downloadedOn = serverDateToLocaleDateString(row.downloadedOn)

            if (downloadedOn.endsWith('1898') || downloadedOn.endsWith('1899'))
              return 'UNKNOWN'

            return downloadedOn
          }

          return '--'
        },
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Edit',
                key: 'EditConnector',
              },
              {
                label: 'Renew Certificate',
                key: 'RenewCertificate',
              },
              {
                label: 'Reinstate Certificate',
                key: 'ReinstateCertificate',
                visible: (row: CustomerInterface) =>
                  row.certificateRevocationStatus !==
                  CertificateRevocationStatus.Ok,
              },
              {
                label: 'Revoke Certificate',
                key: 'RevokeCertificate',
                visible: (row: CustomerInterface) =>
                  row.certificateRevocationStatus !==
                  CertificateRevocationStatus.Revoked,
              },
              {
                label: 'Download Certificate',
                key: 'DownloadCertificate',
                disabled: (row: CustomerInterface) => row.downloadedOn !== null,
              },
              {
                label: 'Certificate History',
                key: 'CertificateHistory',
              },
              {
                label: 'View Connector Health',
                key: 'ConnectorHealth',
              },
              {
                label: 'Edit Configuration',
                key: 'EditConfiguration',
                visible: (row: CustomerInterface) =>
                  canViewInterfaceConfiguration(authUser) ||
                  canManageInterfaceConfiguration(authUser),
              },
              {
                label: 'Edit Import Configuration',
                key: 'EditImportConfiguration',
                visible: (row: CustomerInterface) =>
                  canViewInterfaceConfiguration(authUser) ||
                  canManageInterfaceConfiguration(authUser),
              },
            ],
          },
          {
            options: [
              {
                label: 'Support Dashboard',
                key: 'SupportDashboard',
              },
              {
                label: 'Implementer Logs',
                key: 'ImplementerLogs',
                visible: (_) =>
                  customerContext.currentCustomer?.customer.productLine ===
                  CustomerProductLine.Pro,
              },
              {
                label: 'Nebula Logs',
                key: 'NebulaLogs',
              },
            ],
          },
        ],
      },
    ],
    [customerContext.currentCustomer?.customer, authUser],
  )

  // load the customer details if we don't already have them
  React.useEffect(() => {
    // if the data is new enough then don't bother refetching
    if (
      !considerDataToBeStale(
        customerContext.currentCustomer,
        customerContext.loadingState.lastApiReceiveAt,
      )
    ) {
      return
    }

    dispatch({
      type: 'SetLoadingState',
      payload: 'Loading',
    })

    getCustomerDetails(customerAgencyId)
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          dispatch({
            type: 'LoadCustomerDetails',
            payload: response.data,
            timestamp: Date.now(),
          })
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error loading the customer details',
          })
          dispatch({
            type: 'SetLoadingState',
            payload: 'None',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the customer details',
        })
        dispatch({
          type: 'SetLoadingState',
          payload: 'None',
        })
      })
    // trigger on the current customer.  if the user stops asissting a customer we clear out the customer data
    // so a null here will trigger the staleness check and requery the customer data
  }, [customerContext.currentCustomer?.customer.id])

  React.useEffect(() => {
    if (!customerContext.currentCustomer?.customer) return

    const customer = customerContext.currentCustomer.customer

    if (formFactor.isPhone) {
      dispatchPageTitle({
        type: 'SetPageTitle',
        payload: `${customer.name}`,
      })
    } else {
      dispatchPageTitle({
        type: 'SetPageTitle',
        payload: `${customer.name} | ${getCustomerProductLineLabel(
          customer.productLine,
        )} | ${getConnectorTypeLabel(customer.connectorType)}`,
      })
    }

    return () => {
      dispatchPageTitle({
        type: 'ClearPageTitle',
      })
    }
  }, [customerContext.currentCustomer?.customer.name, formFactor.isPhone])

  defaultAgency.customerAgencyId = params.customerAgencyId
  defaultConnector.customerAgencyId = params.customerAgencyId

  return (
    <React.Fragment>
      {formFactor.isPhone && (
        <PageTitleSubtitle>
          <Typography variant="body1" className={classes.agencySubTitle}>
            {`${getCustomerProductLineLabel(
              customerContext.currentCustomer?.customer.productLine,
            )} | ${getConnectorTypeLabel(
              customerContext.currentCustomer?.customer.connectorType,
            )}`}
          </Typography>
        </PageTitleSubtitle>
      )}
      <PageTitleRightActions>
        <OutlinedButton onClick={() => selectAgencyForEditing(defaultAgency)}>
          Add Agency
        </OutlinedButton>
        <OutlinedButton
          onClick={() => selectConnectorForEditing(defaultConnector)}
          disabled={connectors.length > 0}
        >
          Add Connector
        </OutlinedButton>
      </PageTitleRightActions>
      <Box>
        <ContentSection title="Connectors" disableContentPadding>
          <Table
            filterOptions={CONNECTORS_FILTER_OPTIONS}
            paginationOptions={CONNECTORS_PAGINATION_OPTIONS}
            data={connectors}
            columns={connectorColumns}
            ariaLabel="Connectors for Customer"
            selectedId={editingConnector?.id}
            isLoading={customerContext.loadingState.status === 'Loading'}
            handleMenuClick={handleConnectorMenuClick}
            useDefaultRow
            uniqueIdentifier="CustomerConnectorsList"
          />
        </ContentSection>
        <ContentSection title="Agencies" disableContentPadding>
          <Table
            filterOptions={AGENCIES_FILTER_OPTIONS}
            paginationOptions={AGENCIES_PAGINATION_OPTIONS}
            data={agencies}
            columns={
              agencyColumns as ColumnDef<LicensedAgency, ValidAgencyMenuKey>[]
            }
            ariaLabel="Licensed Agencies for Customer"
            useDefaultRow
            handleMenuClick={
              handleAgencyMenuClick as MenuClick<
                IdentifiableModel,
                ValidAgencyMenuKey
              >
            }
            selectedId={editingAgency?.id}
            isLoading={customerContext.loadingState.status === 'Loading'}
            uniqueIdentifier="CustomerAgenciesList"
          />
        </ContentSection>
      </Box>
      {(isEditingAgency || isAddingAgency) && (
        <SidePanelContent
          anchor="right"
          title={isAddingAgency ? 'Add Agency' : `Edit Agency`}
          onClose={() => selectAgencyForEditing(null)}
        >
          <EditAgency
            key={editingAgency?.id}
            agency={editingAgency as LicensedAgency}
            onClose={stopEditingCustomer}
            agencyDocuments={
              isAddingAgency
                ? customerContext.currentCustomer?.agencyDocuments.filter(
                    (document) =>
                      !agencies.some(
                        (agency) => agency.documentId === document.id,
                      ),
                  ) ?? null
                : customerContext.currentCustomer?.agencyDocuments ?? null
            }
          />
        </SidePanelContent>
      )}
      {(isEditingConnector || (isAddingConnector && connectors.length === 0)) && (
        <SidePanelContent
          anchor="right"
          title={isAddingConnector ? 'Add Connector' : `Edit Connector`}
          onClose={() => selectConnectorForEditing(null)}
        >
          <EditConnector
            key={editingConnector?.id}
            connector={editingConnector as CustomerInterface}
            onClose={stopEditingCustomer}
          />
        </SidePanelContent>
      )}
      {isViewingHealthForConnector && editingConnector !== null && (
        <SidePanelContent
          anchor="right"
          title="Connector Health"
          auditTitle={`${auditTitle} / ${editingConnector.name} / Connector Health`}
          isReadyToAudit={!!auditTitle}
          isSupportPage={true}
          onClose={() =>
            navigate(buildNavLink(customerAgencyId))
          }
        >
          <ConnectorHealth connector={editingConnector as CustomerInterface} />
        </SidePanelContent>
      )}
      {isViewingConfigurationForConnector && editingConnector !== null && (
        <SidePanelContent
          anchor="right"
          title="Override Connector Settings"
          onClose={() =>
            navigate(buildNavLink(customerAgencyId))
          }
        >
          <InterfaceConfiguration
            connector={editingConnector}
            onClose={stopEditingCustomer}
          />
        </SidePanelContent>
      )}
      {isViewingImportConfigurationForConnector && editingConnector !== null && (
        <SidePanelContent
          anchor="right"
          title="Override Import Settings"
          onClose={() =>
            navigate(buildNavLink(customerAgencyId))
          }
        >
          <EditImportConfiguration
            key={customerAgencyId}
            customerAgencyId={customerAgencyId}
            onClose={() =>
              navigate(buildNavLink(customerAgencyId))
            }
            getter={() =>
              getConnectorImportConfiguration(
                customerAgencyId,
                editingConnector?.customerInterfaceId as string,
              )
            }
            updater={(newConfig) =>
              updateConnectorImportConfiguration(
                customerAgencyId,
                editingConnector?.customerInterfaceId as string,
                newConfig,
              )
            }
            unsetFn={() =>
              updateConnectorImportConfiguration(
                customerAgencyId,
                editingConnector?.customerInterfaceId as string,
                null,
              )
            }
          />
        </SidePanelContent>
      )}
      <RenewCertificateModal
        onCancel={() =>
          navigate(buildNavLink(customerAgencyId))
        }
        open={isRenewingCertificateForConnector}
        customerAgencyId={params.customerAgencyId}
        customerInterfaceId={editingConnector?.id as string}
      />
      <DownloadCertificateModal
        onClose={cancelCertDownload}
        open={
          certActionState?.interface !== null &&
          certActionState?.type === 'ConfirmingCertDownload'
        }
        customerAgencyId={params.customerAgencyId}
        customerInterface={certActionState?.interface!}
      />
      <ConfirmationModal
        onCancel={() => setCertActionState(null)}
        onConfirm={() => setCertActionState(null)}
        open={
          certActionState?.interface !== null &&
          certActionState?.type === 'ShowingCertHistoryModal'
        }
        confirmText="Close"
        showCancelButton={false}
        title="Certificate History"
        message={
          <Box>
            {certActionState?.interface.certificateHistory.map(
              (cert: CustomerCertificateViewModel, idx: number) => (
                <CertificateHistory
                  certificate={cert}
                  className={clsx(idx > 0 && classes.certStatus)}
                  key={`CertHistory::${cert.customerCertificateId}`}
                />
              ),
            )}
          </Box>
        }
      />
      {certActionState?.interface !== null &&
        certActionState?.type === 'ConfirmingCertRevoke' && (
          <CertificateRevocationStatusModal
            certStatus={CertificateRevocationStatus.Revoked}
            connector={certActionState?.interface}
            onCancel={() => setCertActionState(null)}
            onConfirm={() => setCertActionState(null)}
            open
          />
        )}
      {certActionState?.interface !== null &&
        certActionState?.type === 'ConfirmingCertReinstate' && (
          <CertificateRevocationStatusModal
            certStatus={CertificateRevocationStatus.Ok}
            connector={certActionState?.interface}
            onCancel={() => setCertActionState(null)}
            onConfirm={() => setCertActionState(null)}
            open
          />
        )}
    </React.Fragment>
  )
}

export const CustomerDetailsPage: PageDefinition = {
  routes: () => [
    '/customers/:customerAgencyId/details/',
    '/customers/:customerAgencyId/details/:action',
    '/customers/:customerAgencyId/details/agency/:licensedAgencyId',
    '/customers/:customerAgencyId/details/connector/:customerInterfaceId',
    '/customers/:customerAgencyId/details/connector/:customerInterfaceId/:action',
  ],
  type: [PageType.Nested],
  sortOrder: () => 0,  
  component: <CustomerDetails />,
  pageTitle: (user) => {
    return `Customer Detail`
  },
  navLinkText: 'Details',
  canAccess: (user) => {
    return user !== null && user.isSupport
  },
  buildNavLink: (user) => '/customers/:customerAgencyId/details',
}
